import { Fragment, useEffect, useRef, useState } from "react";
import CountdownTimer from "./CountdownTimer";
import { BADGE_STATUSES, statusLabel, statusColor } from "./constants";
import ApproveDenyModal from "../../components/RootElements/ApproveDenyModal";
import OmtorkModal from "./OmtorkModal";
import DateNavBar, { toLocalDate, atNoon, getWeekBounds, groupHeading } from "./DateNavBar";

const noteStatusColor = (status) =>
    ({ planned: "bg-blue-100 text-blue-700", ongoing: "bg-amber-100 text-amber-700" }[status] || "");

const noteStatusLabel = (status) =>
    ({ planned: "Planerad", ongoing: "Pågående" }[status] || status);

const TypeDot = ({ noteTypes, type }) => {
    const color = (noteTypes || []).find(nt => nt.name === type)?.color || "#9CA3AF";
    return <span className="inline-block w-2 h-2 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: color }} />;
};

const TorkCell = ({ torkName, tork, noteTypes }) => {
    const [hovered, setHovered] = useState(false);
    const activeNotes = (tork?.notes || []).filter(n => n.status === "planned" || n.status === "ongoing");

    if (!activeNotes.length) return <span className="font-medium">{torkName}</span>;

    return (
        <div
            className="relative inline-flex items-center gap-1 cursor-default"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <span className="font-medium">{torkName}</span>
            <span className="text-amber-500 text-xs leading-none" title="Aktiva noteringar">⚠</span>
            {hovered && (
                <div className="absolute left-0 top-full mt-1 z-30 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs">
                    <p className="font-semibold text-gray-700 mb-1.5">Noteringar</p>
                    <ul className="space-y-1.5">
                        {activeNotes.map(n => (
                            <li key={n.id} className="flex items-start gap-1.5 text-gray-600">
                                <TypeDot noteTypes={noteTypes} type={n.type} />
                                <span className="flex-1">{n.type} – {n.note}</span>
                                <span className={"flex-shrink-0 px-1.5 py-0.5 rounded " + noteStatusColor(n.status)}>
                                    {noteStatusLabel(n.status)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

const torkById = (torks, torkId) => torks.find(t => t.id === torkId);
const torkName = (torks, torkId) => torkById(torks, torkId)?.name || "–";

// Status buttons shown in the table — exclude "ready", "closed", and "picking"
const ACTION_STATUSES = BADGE_STATUSES.filter(s => s !== "ready" && s !== "closed" && s !== "picking");

const OrderRow = ({
    o, torks, noteTypes, readOnly,
    onStatusClick, onRetorkClick,
    openMenuId, menuRef, onMenuToggle, onShowHistory, onResetToOrders,
}) => (
    <tr className="border-b hover:bg-gray-50">
        <Td>
            <TorkCell
                torkName={torkName(torks, o.torkId)}
                tork={torkById(torks, o.torkId)}
                noteTypes={noteTypes}
            />
        </Td>
        <Td>{o.package?.dimension || "–"}</Td>
        <Td>{o.package?.moisture || "–"}</Td>
        <Td>{o.type || "–"}</Td>
        <Td><CountdownTimer target={o.dates?.dryFinished} /></Td>
        <Td>{o.dates?.dryFinished
            ? new Date(o.dates.dryFinished).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })
            : "–"}
        </Td>
        {!readOnly && (
        <Td>
            <div className="flex flex-wrap gap-1.5">
                {ACTION_STATUSES.map(status => (
                    <button
                        key={status}
                        onClick={() => onStatusClick(o.id, status)}
                        disabled={o.status === status}
                        className={
                            "px-2.5 py-1 rounded-full text-xs border transition " +
                            (o.status === status
                                ? statusColor(status) + " border-transparent font-semibold opacity-60 cursor-default"
                                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100")
                        }
                    >
                        {statusLabel(status)}
                    </button>
                ))}
                <button
                    onClick={() => onRetorkClick(o)}
                    className="px-2.5 py-1 rounded-full text-xs border transition bg-white text-amber-700 border-amber-400 hover:bg-amber-50"
                >
                    Omtork
                </button>
            </div>
        </Td>
        )}
        {!readOnly && (
        <Td>
            <div className="relative" ref={openMenuId === o.id ? menuRef : null}>
                <button
                    onClick={() => onMenuToggle(o.id)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                    title="Fler alternativ"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <circle cx="10" cy="4" r="1.5" />
                        <circle cx="10" cy="10" r="1.5" />
                        <circle cx="10" cy="16" r="1.5" />
                    </svg>
                </button>
                {openMenuId === o.id && (
                    <div className="absolute right-0 z-10 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                        <button
                            onClick={() => onShowHistory(o)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                        >
                            Visa historik
                        </button>
                        <button
                            onClick={() => onResetToOrders(o.id)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                        >
                            Flytta till Ordrar
                        </button>
                    </div>
                )}
            </div>
        </Td>
        )}
    </tr>
);

const TableHead = ({ readOnly }) => (
    <thead className="bg-gray-100 text-gray-600 text-left">
        <tr>
            <Th>Tork</Th>
            <Th>Dim</Th>
            <Th>Fukt</Th>
            <Th>Typ</Th>
            <Th>Tid kvar</Th>
            <Th>Klar datum</Th>
            {!readOnly && <Th>Ändra status</Th>}
            {!readOnly && <Th></Th>}
        </tr>
    </thead>
);

/* Orders currently in the tork (any badge status). Table view with status controls. */
const KilnsToEmpty = ({ orders, torks, noteTypes = [], onStatusChange, onRetork, onShowHistory, readOnly = false }) => {
    const [confirmPickedId, setConfirmPickedId] = useState(null);
    const [confirmResetId, setConfirmResetId] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [retorkOrder, setRetorkOrder] = useState(null);
    const [viewMode, setViewMode] = useState("day");
    const [anchorDate, setAnchorDate] = useState(() => toLocalDate(new Date()));
    const menuRef = useRef(null);

    useEffect(() => {
        if (!openMenuId) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [openMenuId]);

    const today = toLocalDate(new Date());

    const inTork = orders.filter(o => BADGE_STATUSES.includes(o.status) && o.status !== "closed");
    const byFinish = (a, b) => new Date(a.dates.dryFinished) - new Date(b.dates.dryFinished);
    const dated = inTork.filter(o => o.dates?.dryFinished).sort(byFinish);
    const undated = inTork.filter(o => !o.dates?.dryFinished);

    let visible = dated;
    if (viewMode === "day") {
        visible = dated.filter(o => toLocalDate(new Date(o.dates.dryFinished)) === anchorDate);
    } else if (viewMode === "week") {
        const { mon, sun } = getWeekBounds(atNoon(anchorDate));
        visible = dated.filter(o => {
            const d = new Date(o.dates.dryFinished);
            return d >= mon && d <= sun;
        });
    }

    // Ordered day-groups for week view (only days that have orders)
    const dayGroups = [];
    if (viewMode === "week") {
        for (const o of visible) {
            const date = toLocalDate(new Date(o.dates.dryFinished));
            const group = dayGroups[dayGroups.length - 1];
            if (group && group.date === date) group.orders.push(o);
            else dayGroups.push({ date, orders: [o] });
        }
    }

    const handleClick = (orderId, status) => {
        if (status === "picked") {
            setConfirmPickedId(orderId);
        } else {
            onStatusChange(orderId, status);
        }
    };

    const handleMenuToggle = (orderId) => {
        setOpenMenuId(prev => (prev === orderId ? null : orderId));
    };

    const handleShowHistory = (order) => {
        setOpenMenuId(null);
        onShowHistory(order);
    };

    const handleResetToOrders = (orderId) => {
        setOpenMenuId(null);
        setConfirmResetId(orderId);
    };

    const colSpan = readOnly ? 6 : 8;

    const rowProps = {
        torks, noteTypes, readOnly,
        onStatusClick: handleClick,
        onRetorkClick: setRetorkOrder,
        openMenuId, menuRef,
        onMenuToggle: handleMenuToggle,
        onShowHistory: handleShowHistory,
        onResetToOrders: handleResetToOrders,
    };

    return (
        <>
        <DateNavBar
            viewMode={viewMode}
            anchorDate={anchorDate}
            onViewModeChange={setViewMode}
            onAnchorDateChange={setAnchorDate}
        />

        {visible.length === 0 ? (
            <p className="text-gray-500 p-4">
                {inTork.length === 0
                    ? "Inga torkar att tömma just nu."
                    : viewMode === "week"
                        ? "Inga ordrar denna vecka."
                        : viewMode === "day"
                            ? "Inga ordrar matchar valt datum."
                            : "Inga ordrar med klar-datum."}
            </p>
        ) : (
        <div className="w-full">
            <table className="min-w-full text-sm">
                <TableHead readOnly={readOnly} />
                <tbody>
                    {viewMode === "week"
                        ? dayGroups.map(group => (
                            <Fragment key={group.date}>
                                <tr>
                                    <td
                                        colSpan={colSpan}
                                        className={
                                            "px-3 py-2 text-sm font-semibold border-b " +
                                            (group.date === today
                                                ? "bg-blue-50 text-blue-700"
                                                : "bg-gray-50 text-gray-700")
                                        }
                                    >
                                        {(group.date === today ? "Idag · " : "") + groupHeading(group.date)} ({group.orders.length})
                                    </td>
                                </tr>
                                {group.orders.map(o => <OrderRow key={o.id} o={o} {...rowProps} />)}
                            </Fragment>
                        ))
                        : visible.map(o => <OrderRow key={o.id} o={o} {...rowProps} />)}
                </tbody>
            </table>
        </div>
        )}

        {/* ── Orders without a finish date ───────────────── */}
        {undated.length > 0 && (
            <div className="mt-6">
                <p className="text-sm font-semibold text-gray-500 mb-2">Utan klar-datum ({undated.length})</p>
                <table className="min-w-full text-sm">
                    <TableHead readOnly={readOnly} />
                    <tbody>
                        {undated.map(o => <OrderRow key={o.id} o={o} {...rowProps} />)}
                    </tbody>
                </table>
            </div>
        )}

        {confirmPickedId && (
            <ApproveDenyModal
                onAccept={() => { onStatusChange(confirmPickedId, "closed"); setConfirmPickedId(null); }}
                onDeny={() => setConfirmPickedId(null)}
            >
                Vill du markera ordern som tömd? Detta avslutar ordern.
            </ApproveDenyModal>
        )}
        {confirmResetId && (
            <ApproveDenyModal
                onAccept={() => { onStatusChange(confirmResetId, "closednotstarted"); setConfirmResetId(null); }}
                onDeny={() => setConfirmResetId(null)}
            >
                Vill du flytta tillbaka ordern till Ordrar? Status sätts till "Stängd ej startad" och torktid återställs.
            </ApproveDenyModal>
        )}
        {retorkOrder && (
            <OmtorkModal
                order={retorkOrder}
                torks={torks}
                noteTypes={noteTypes}
                onSubmit={(id, payload) => { onRetork(id, payload); setRetorkOrder(null); }}
                onClose={() => setRetorkOrder(null)}
            />
        )}
        </>
    );
};

const Th = ({ children }) => <th className="px-3 py-2 font-medium">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-2">{children}</td>;

export default KilnsToEmpty;
